import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Settings, fetchSettings } from '../api/settings';
import { dataPreloader } from '../utils/dataPreloader';

interface SettingsContextType {
    settings: Settings | null;
    isLoading: boolean;
    error: string | null;
    getSetting: (key: keyof Settings, defaultValue?: string) => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
    children: ReactNode;
    preloadedSettings?: Settings | null;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
    children,
    preloadedSettings
}) => {
    const [settings, setSettings] = useState<Settings | null>(preloadedSettings || null);
    const [isLoading, setIsLoading] = useState(!preloadedSettings);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // If we already have preloaded settings, skip fetching
        if (preloadedSettings) {
            console.log('[SettingsContext] Using preloaded settings');
            return;
        }

        // Otherwise, try to get from preloader cache or fetch
        const loadSettings = async () => {
            try {
                // Check if preloader has cached data
                const preloadedData = dataPreloader.getPreloadedData();
                if (preloadedData?.settings) {
                    console.log('[SettingsContext] Using cached settings from preloader');
                    setSettings(preloadedData.settings);
                } else {
                    console.log('[SettingsContext] Fetching settings from API');
                    const data = await fetchSettings();
                    setSettings(data);
                }
            } catch (err) {
                console.error('Failed to load settings:', err);
                setError('Failed to load settings');
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, [preloadedSettings]);

    const getSetting = (key: keyof Settings, defaultValue: string = '') => {
        if (!settings) return defaultValue;
        return settings[key] || defaultValue;
    };

    return (
        <SettingsContext.Provider value={{ settings, isLoading, error, getSetting }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
